import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, Heart, HeartPulse, PawPrint, Skull, TrendingUp, Clock, Wallet, Coins, Trees } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import api from "@/lib/api";

function StatCard({ icon: Icon, label, value, testId, color = "text-amtm-teal" }) {
  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-5" data-testid={testId}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-amtm-text">{value}</p>
      <p className="text-sm text-amtm-textMuted">{label}</p>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((res) => setData(res.data));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <StaffHeader />
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-amtm-teal" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-5xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-6">Analytics Dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-stat-grid">
          <StatCard icon={PawPrint} label="Total Cases" value={data.total_cases} testId="stat-total-cases" />
          <StatCard icon={HeartPulse} label="Under Care" value={data.active_count} testId="stat-active-count" color="text-amtm-info" />
          <StatCard icon={Heart} label="Released" value={data.released_count} testId="stat-released-count" color="text-amtm-success" />
          <StatCard icon={Heart} label="Adopted" value={data.adoption_count} testId="stat-adopted-count" color="text-amtm-success" />
          <StatCard icon={Trees} label="Sanctuary Residents" value={data.sanctuary_count} testId="stat-sanctuary-count" color="text-amtm-teal" />
          <StatCard icon={Skull} label="Passed Away" value={data.passed_away_count} testId="stat-passed-away-count" color="text-amtm-textMuted" />
          <StatCard icon={TrendingUp} label="Recovery Rate" value={`${data.recovery_rate}%`} testId="stat-recovery-rate" />
          <StatCard icon={Clock} label="Avg Days Under Care" value={data.avg_days_under_care} testId="stat-avg-days" />
          <StatCard icon={Wallet} label="Needed (Active Cases)" value={`₹${data.total_donation_needed_active.toLocaleString("en-IN")}`} testId="stat-donation-needed" />
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-5 flex items-center gap-3" data-testid="stat-total-donations">
            <Coins className="h-8 w-8 text-amtm-success" />
            <div>
              <p className="text-2xl font-bold text-amtm-text">₹{data.total_donations_received.toLocaleString("en-IN")}</p>
              <p className="text-sm text-amtm-textMuted">Total Donations Received</p>
            </div>
          </div>
          <div className="bg-amtm-teal/10 border border-amtm-teal/30 rounded-2xl p-5 flex items-center gap-3" data-testid="stat-total-monthly-care-need">
            <Trees className="h-8 w-8 text-amtm-teal" />
            <div>
              <p className="text-2xl font-bold text-amtm-text">₹{data.total_monthly_care_need.toLocaleString("en-IN")}</p>
              <p className="text-sm text-amtm-textMuted">Total Monthly Care Need</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-5" data-testid="chart-cases-per-month">
            <h3 className="font-display text-lg mb-4">Cases Per Month</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.cases_per_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE7DD" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#F5A623" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-5" data-testid="chart-cases-per-staff">
            <h3 className="font-display text-lg mb-4">Cases Logged Per Staff</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.cases_per_staff} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE7DD" />
                <XAxis type="number" fontSize={12} allowDecimals={false} />
                <YAxis dataKey="staff_name" type="category" fontSize={12} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#0288D1" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
